/**
 * Header & navigation
 * <announcement-bar>  keeps --announcement-height current, rotates messages on mobile
 * <site-header>       keeps --header-height current, sticky "scroll-up" mode, mega menus
 * <mobile-menu>       expand / collapse toggles inside the menu drawer
 */
(() => {
  const root = document.documentElement;

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.resizeObserver = new ResizeObserver(() => {
        root.style.setProperty('--announcement-height', `${this.offsetHeight}px`);
      });
      this.resizeObserver.observe(this);

      if (!this.classList.contains('announcement-bar--rotate')) return;

      // Only the messages that are visible on mobile take part in the rotation.
      this.items = [...this.querySelectorAll('.announcement-bar__item:not(.announcement-bar__item--desktop)')];
      this.media = window.matchMedia('(max-width: 749px) and (prefers-reduced-motion: no-preference)');
      this.start();

      // Theme editor: show the selected message and hold it until it is deselected.
      this.addEventListener('shopify:block:select', (event) => {
        clearInterval(this.timer);
        if (this.items.includes(event.target)) this.show(event.target);
      });
      this.addEventListener('shopify:block:deselect', () => this.start());
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      clearInterval(this.timer);
      root.style.setProperty('--announcement-height', '0px');
    }

    start() {
      clearInterval(this.timer);
      this.timer = setInterval(() => this.next(), 4000);
    }

    show(item) {
      this.items.forEach((el) => el.classList.toggle('is-active', el === item));
    }

    next() {
      if (!this.media.matches) return;
      const index = this.items.findIndex((el) => el.classList.contains('is-active'));
      this.show(this.items[(index + 1) % this.items.length]);
    }
  }

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      // The section wrapper is the sticky element, so it carries the hidden state.
      this.wrapper = this.closest('.shopify-section') || this;
      this.activeItem = null;
      this.lastScrollY = window.scrollY;
      this.scrollingDown = false;

      this.resizeObserver = new ResizeObserver(() => {
        root.style.setProperty('--header-height', `${this.offsetHeight}px`);
        this.fit();
      });
      this.resizeObserver.observe(this);
      document.fonts.ready.then(() => this.fit());
      if (!document.querySelector('announcement-bar')) root.style.setProperty('--announcement-height', '0px');

      this.querySelectorAll('[data-mega]').forEach((item) => this.setupMega(item));

      this.onScroll = this.onScroll.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      this.addEventListener('focusin', () => this.wrapper.classList.remove('is-hidden'));
      this.addEventListener('focusout', (event) => {
        if (this.activeItem && event.relatedTarget && !this.activeItem.contains(event.relatedTarget)) this.closeMega();
      });

      // Theme editor: reveal the mega menu that holds the selected promo block.
      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('[data-mega]');
        if (item) this.openMega(item);
      });
      this.addEventListener('shopify:block:deselect', () => this.closeMega());
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.wrapper.classList.remove('is-hidden');
    }

    // A menu too long for one row moves to its own row (.header--stacked); if even that
    // overflows, the header falls back to the burger + drawer (.header--compact).
    fit() {
      const inner = this.querySelector('.header__inner');
      const actions = this.querySelector('.header__actions');
      const nav = this.querySelector('.header__nav');
      this.classList.remove('header--compact', 'header--stacked');
      if (!nav || getComputedStyle(nav).display === 'none') return;
      if (this.dataset.menuPosition === 'below') {
        this.classList.add('header--stacked');
        return;
      }
      // Centered menu wraps onto a second row by itself (CSS); nothing to measure.
      if (this.dataset.menuPosition === 'center') return;

      const overflows = () => {
        const limit = inner.getBoundingClientRect().right - parseFloat(getComputedStyle(inner).paddingRight);
        return nav.scrollWidth > nav.clientWidth + 1 || actions.getBoundingClientRect().right > limit + 1;
      };

      if (!overflows()) return;
      this.classList.add('header--stacked');
    }

    setupMega(item) {
      // Hover with a short intent delay; leaving the item (link + panel) closes it.
      item.addEventListener('mouseenter', () => this.schedule(() => this.openMega(item), 100));
      item.addEventListener('mouseleave', () => this.schedule(() => this.closeMega(), 150));

      item.querySelector('.header__toggle').addEventListener('click', () => {
        if (this.activeItem === item) this.closeMega();
        else this.openMega(item);
      });

      item.querySelector('.header__link').addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.openMega(item);
        const first = item.querySelector('.mega-menu a');
        if (first) first.focus();
      });
    }

    schedule(callback, delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(callback, delay);
    }

    openMega(item) {
      clearTimeout(this.timer);
      if (this.activeItem === item) return;
      this.closeMega();
      this.activeItem = item;
      item.classList.add('is-open');
      item.querySelector('.header__toggle').setAttribute('aria-expanded', 'true');
      this.classList.add('is-mega-open');
    }

    closeMega() {
      clearTimeout(this.timer);
      if (!this.activeItem) return;
      this.activeItem.classList.remove('is-open');
      this.activeItem.querySelector('.header__toggle').setAttribute('aria-expanded', 'false');
      this.activeItem = null;
      this.classList.remove('is-mega-open');
    }

    onDocumentClick(event) {
      if (this.activeItem && !this.activeItem.contains(event.target)) this.closeMega();
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.activeItem) return;
      const toggle = this.activeItem.querySelector('.header__toggle');
      const focusInside = this.activeItem.querySelector('.mega-menu').contains(document.activeElement);
      this.closeMega();
      if (focusInside) toggle.focus();
    }

    onScroll() {
      const scrollY = Math.max(window.scrollY, 0);
      if (Math.abs(scrollY - this.lastScrollY) < 8) return;

      const down = scrollY > this.lastScrollY;
      this.lastScrollY = scrollY;

      if (down !== this.scrollingDown) {
        this.scrollingDown = down;
        this.closeMega();
      }

      if (this.dataset.sticky === 'scroll-up') {
        this.wrapper.classList.toggle('is-hidden', down && scrollY > this.offsetHeight * 2);
      }
      if (this.hasAttribute('data-transparent')) {
        this.wrapper.classList.toggle('is-scrolled', scrollY > 24);
      }
    }
  }

  class MobileMenu extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('.mobile-menu__toggle');
        if (!toggle) return;
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        toggle.closest('.mobile-menu__item').classList.toggle('is-open', open);
      });
    }
  }

  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
  if (!customElements.get('mobile-menu')) customElements.define('mobile-menu', MobileMenu);
})();
